import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import heroTennis from "@/assets/hero-tennis.jpg";
import heroPadel from "@/assets/hero-padel.jpg";
import heroPickleball from "@/assets/hero-pickleball.jpg";

interface MyEventCardVerticalProps {
  event: {
    id: string;
    registrationId?: string;
    title: string;
    sport: string;
    startDate: string;
    category: string;
    status: "upcoming" | "active" | "finished";
    imageUrl?: string;
    isLeague?: boolean;
  };
}

const getDefaultImage = (sport: string) => {
  switch (sport.toLowerCase()) {
    case 'padel': return heroPadel;
    case 'tenis':
    case 'tennis': return heroTennis;
    case 'pickleball': return heroPickleball;
    default: return heroPadel;
  }
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'active': return { label: 'En Curso', className: 'bg-green-500 text-white' };
    case 'upcoming': return { label: 'Próximo', className: 'bg-secondary text-secondary-foreground' };
    case 'finished': return { label: 'Finalizado', className: 'bg-gray-500 text-white' };
    default: return { label: status, className: 'bg-secondary text-secondary-foreground' };
  }
};

export function MyEventCardVertical({ event }: MyEventCardVerticalProps) {
  const navigate = useNavigate();
  const statusInfo = getStatusBadge(event.status);
  const detailsPath = event.isLeague ? `/league/${event.id}` : `/tournament/${event.id}`;

  const formatShortDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
    });
  };

  return (
    <Card
      className="min-w-[150px] max-w-[150px] overflow-hidden cursor-pointer hover:shadow-md transition-shadow border-border/50 flex-shrink-0"
      onClick={() => navigate(detailsPath)}
    >
      {/* Image with status badge */}
      <div className="relative">
        <img
          src={event.imageUrl || getDefaultImage(event.sport)}
          alt={event.title}
          className="w-full h-[100px] object-cover"
        />
        <Badge className={`absolute top-1.5 left-1.5 text-[9px] px-1.5 py-0 ${statusInfo.className}`}>
          {statusInfo.label}
        </Badge>
      </div>

      {/* Info */}
      <div className="p-2 space-y-1">
        <h4 className="font-semibold text-xs leading-tight line-clamp-2">
          {event.title}
        </h4>
        <p className="text-[10px] text-muted-foreground truncate">
          {event.sport} • {formatShortDate(event.startDate)}
        </p>
        <Badge variant="outline" className="text-[9px] px-1 py-0">
          {event.category}
        </Badge>
      </div>
    </Card>
  );
}
